/**
 * Custom hook for fetching prehire candidates filtered by role.
 * Calls POST /api/PrehireSearch/search with an optional roles filter
 * to retrieve candidates visible under a specific role tab.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { PrehireDto, ApiException, RoleGroupedPrehireResult, PrehireQueryOptions, WorkOnBehalfEntry } from '../../generated/api.onboarding.client';
import { createApiClient } from '../api/apiClient';
import { useNoAccess } from '../../context';
import { isTestMode } from '../../config';
import { getMockGroupedPrehireResult } from './mockPrehireData';

/**
 * Error messages for different scenarios
 */
const ERROR_MESSAGES = {
    NO_ACCESS:
        'You do not have access to view prehire candidates. Please contact your administrator.',
    SESSION_EXPIRED: 'Session expired. Please refresh the page.',
    GENERAL_ERROR: 'We ran into an issue while loading candidates. Please try again later.',
};

interface UsePrehiresDataResult {
    data: PrehireDto[] | null;
    groupedData: RoleGroupedPrehireResult | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

const getPrehireKey = (prehire: PrehireDto, index: number): string => {
    return prehire.crmCaseId
        || `${prehire.candidate?.email ?? ''}-${prehire.candidate?.fullName ?? ''}-${index}`;
};

const uniquePrehires = (prehires: PrehireDto[]): PrehireDto[] => {
    const seen = new Set<string>();
    return prehires.filter((prehire, index) => {
        const key = getPrehireKey(prehire, index);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const mergeWorkOnBehalfEntries = (entries: WorkOnBehalfEntry[]): WorkOnBehalfEntry[] => {
    const byEmail = new Map<string, WorkOnBehalfEntry>();

    for (const entry of entries) {
        const email = entry.hiringManagerEmail?.trim();
        if (!email) continue;

        const existing = byEmail.get(email.toLowerCase());
        if (!existing) {
            byEmail.set(email.toLowerCase(), {
                hiringManagerEmail: email,
                prehires: uniquePrehires([...(entry.prehires ?? [])]),
            });
            continue;
        }

        existing.prehires = uniquePrehires([...(existing.prehires ?? []), ...(entry.prehires ?? [])]);
    }

    return Array.from(byEmail.values());
};

const mergeRoleGroupedResults = (results: RoleGroupedPrehireResult[]): RoleGroupedPrehireResult => {
    const merged: RoleGroupedPrehireResult = {};

    const manager = uniquePrehires(results.flatMap(result => result.manager ?? []));
    const onboardingContact = uniquePrehires(results.flatMap(result => result.onboardingContact ?? []));
    const recruiter = uniquePrehires(results.flatMap(result => result.recruiter ?? []));
    const workOnBehalf = mergeWorkOnBehalfEntries(results.flatMap(result => result.workOnBehalf ?? []));

    if (manager.length > 0) merged.manager = manager;
    if (onboardingContact.length > 0) merged.onboardingContact = onboardingContact;
    if (recruiter.length > 0) merged.recruiter = recruiter;
    if (workOnBehalf.length > 0) merged.workOnBehalf = workOnBehalf;

    return merged;
};

/**
 * Hook to fetch prehire candidates, optionally filtered by a single role.
 * When a role is provided, the search request includes `roles: [role]`
 * so only data for that role is returned.
 */
export const usePrehiresData = (role?: string, targetHiringManagerEmails?: string[]): UsePrehiresDataResult => {
    const msalContext = useMsal();
    const noAccessContext = useNoAccess();
    const noAccessRef = useRef(noAccessContext);
    noAccessRef.current = noAccessContext;

    const instance = isTestMode ? null : msalContext?.instance;
    const accounts = isTestMode ? [] : msalContext?.accounts || [];

    const [data, setData] = useState<PrehireDto[] | null>(null);
    const [groupedData, setGroupedData] = useState<RoleGroupedPrehireResult | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const account = accounts[0] || null;

    const fetchData = useCallback(async () => {
        // Skip the search call entirely when no role is provided
        // (user has no valid roles or roles are still loading)
        if (!role) {
            setLoading(false);
            return;
        }

        if (!isTestMode && !account) {
            setLoading(false);
            setError('No authenticated account found');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const normalizedManagerEmails = Array.from(
                new Set(
                    (targetHiringManagerEmails ?? [])
                        .map(email => email.trim())
                        .filter(Boolean),
                ),
            );

            // In test mode, use mock data instead of calling the API
            const result: RoleGroupedPrehireResult = isTestMode
                ? role === 'WorkOnBehalf' && normalizedManagerEmails.length > 0
                    ? mergeRoleGroupedResults(
                          normalizedManagerEmails.map(email => getMockGroupedPrehireResult(role, email)),
                      )
                    : getMockGroupedPrehireResult(role)
                : role === 'WorkOnBehalf' && normalizedManagerEmails.length > 0
                    ? mergeRoleGroupedResults(
                          await Promise.all(
                              normalizedManagerEmails.map(async email => createApiClient(instance, account).search(
                                  ({
                                      sortBy: 'firstname',
                                      sortDirection: 'desc',
                                      search: '',
                                      roles: ['workOnBehalf'],
                                      TargetHiringManagerEmail: email,
                                  } as PrehireQueryOptions & { TargetHiringManagerEmail?: string }),
                              )),
                          ),
                      )
                    : await createApiClient(instance, account).search(
                          role === 'WorkOnBehalf'
                              ? ({
                                    sortBy: 'firstname',
                                    sortDirection: 'desc',
                                    search: '',
                                    roles: ['workOnBehalf'],
                                } as PrehireQueryOptions)
                              : ({
                                    sortDirection: 'asc',
                                    roles: role ? [role] : undefined,
                                } as PrehireQueryOptions),
                      );

            // Store the role-grouped result for tab filtering
            setGroupedData(result);

            // Combine all prehires from role-grouped result into a single list
            const allPrehires: PrehireDto[] = [
                ...(result.manager ?? []),
                ...(result.onboardingContact ?? []),
                ...(result.recruiter ?? []),
                ...(result.workOnBehalf ?? []).flatMap(w => w.prehires ?? []),
            ];

            // De-duplicate by crmCaseId
            const unique = uniquePrehires(allPrehires);

            setData(unique);
        } catch (err: unknown) {
            console.error('Error fetching prehires data:', err);

            // Detect 404 from any error shape thrown by NSwag client:
            // - ApiException with status 404
            // - ProblemDetails object with status 404
            // - String containing "not found" (from NotFound(string) responses)
            // - SyntaxError from JSON.parse when API returns plain text 404 (e.g. "No prehire...")
            const errStatus = ApiException.isApiException(err)
                ? (err as ApiException).status
                : (typeof err === 'object' && err !== null && 'status' in err)
                    ? (err as { status?: number }).status
                    : undefined;

            const isNotFound = errStatus === 404
                || (typeof err === 'string' && err.toLowerCase().includes('not found'))
                || (err instanceof SyntaxError && err.message.includes('No prehire'));

            if (isNotFound) {
                // No prehires found — show empty state, not error page
                setData([]);
            } else if (ApiException.isApiException(err)) {
                const apiError = err as ApiException;

                switch (apiError.status) {
                    case 401:
                        setError(ERROR_MESSAGES.SESSION_EXPIRED);
                        break;
                    case 403:
                        if (!isTestMode && noAccessRef.current?.onNoAccess) {
                            noAccessRef.current.onNoAccess(ERROR_MESSAGES.NO_ACCESS);
                        }
                        return;
                    case 500:
                    default:
                        if (!isTestMode && noAccessRef.current?.onNoAccess) {
                            noAccessRef.current.onNoAccess(ERROR_MESSAGES.GENERAL_ERROR);
                        } else {
                            setError(ERROR_MESSAGES.GENERAL_ERROR);
                        }
                        return;
                }
            } else {
                if (!isTestMode && noAccessRef.current?.onNoAccess) {
                    noAccessRef.current.onNoAccess(ERROR_MESSAGES.GENERAL_ERROR);
                } else {
                    setError(ERROR_MESSAGES.GENERAL_ERROR);
                }
            }
        } finally {
            setLoading(false);
        }
    }, [instance, account, role, targetHiringManagerEmails]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        groupedData,
        loading,
        error,
        refetch: fetchData,
    };
};
